import type {
  BrandSpecialPrices,
  BrandSpecialUserSegmentIds,
  ProductCategory,
} from '@iheartjane/types';
import type { Dict as MixpanelProperties } from 'mixpanel-browser';

// Account for the fact that the AlgoliaProduct type is readonly
// and that's often what's getting passed in to the tracking functions
interface TrackingBrandSpecialUserSegmentIds {
  applicable_brand_specials_excluded_user_segments?:
    BrandSpecialUserSegmentIds | readonly number[];
  applicable_brand_specials_included_user_segments?:
    BrandSpecialUserSegmentIds | readonly number[];
}

interface CoreProduct extends TrackingBrandSpecialUserSegmentIds {
  aggregate_rating?: number | null;
  applicable_brand_special_ids?: readonly (number | string)[] | null;
  applicable_special_ids?: readonly number[] | null;
  available_weights?: readonly string[] | null;
  brand?: string | null;
  brand_special_prices?: BrandSpecialPrices | null;
  brand_subtype?: string | null;
  category?: ProductCategory | null;
  has_brand_discount?: boolean | null;
  id?: number | string | null;
  kind?: string | null;
  name?: string;
  percent_cbd?: number | string | null;
  percent_thc?: number | string | null;
  primaryPercentCBD?: number | string | null;
  primaryPercentTHC?: number | string | null;
  product_brand_id?: number | string | null;
  product_id?: number | string | null;
  review_count?: number | null;
  root_subtype?: string | null;
  special_id?: number | null;
}

const countDecimals = (num: number): number => {
  if (Math.floor(num.valueOf()) === num.valueOf()) return 0;
  return num.toString().split('.')[1]?.length ?? 0;
};

const isEmpty = (val: unknown): boolean => {
  if (val === null || val === undefined) return true;
  if (typeof val === 'string') return val.length === 0;
  if (Array.isArray(val)) return val.length === 0;
  if (typeof val === 'object') return Object.keys(val).length === 0;
  return false;
};

/**
 * @param properties a hash of properties associated with a Mixpanel event
 * @returns a hash with all values normalized. Strings are downcased and numbers are truncated to 2 decimal places.
 */
export const normalizeMixpanelPropertyValues = (
  properties: MixpanelProperties = {}
) => {
  const normalizePrimitives = (val: string | number | object) => {
    // Lowercase strings
    if (typeof val === 'string') {
      return val.toLowerCase();
      // Truncate decimal numbers with more than 2 decimal places
    } else if (typeof val === 'number' && countDecimals(val) > 2) {
      return val.toFixed(2);
    }

    return val;
  };

  return Object.entries(properties).reduce(
    (acc: MixpanelProperties, [k, v]) => {
      if (typeof v !== 'number' && typeof v !== 'boolean' && isEmpty(v)) {
        // handles empty {}, [], & "" as well as undefined & null, but also 0/false so escaping that.
        acc[k] = null;
      } else if (Array.isArray(v)) {
        acc[k] = v.map(normalizePrimitives);
      } else {
        acc[k] = normalizePrimitives(v);
      }

      return acc;
    },
    {}
  );
};

const shouldProductShowSpecial = (
  product: TrackingBrandSpecialUserSegmentIds,
  userSegments?: number[]
): boolean => {
  const ALGOLIA_NO_SEGMENTS = -1;
  const includedSegments =
    product.applicable_brand_specials_included_user_segments;
  const excludedSegments =
    product.applicable_brand_specials_excluded_user_segments;

  const noIncludedSegments =
    !includedSegments ||
    includedSegments === ALGOLIA_NO_SEGMENTS ||
    !includedSegments.length;
  const noExcludedSegments =
    !excludedSegments ||
    excludedSegments === ALGOLIA_NO_SEGMENTS ||
    !excludedSegments.length;

  if (!userSegments || userSegments.length === 0) {
    return noIncludedSegments;
  }

  const userIsIncluded = noIncludedSegments
    ? true
    : userSegments.some((segmentId) => includedSegments?.includes(segmentId));

  const userIsExcluded = noExcludedSegments
    ? false
    : userSegments.some((segmentId) => excludedSegments?.includes(segmentId));

  return userIsIncluded && !userIsExcluded;
};

export const coreProductProperties = (
  product: CoreProduct,
  userSegments?: number[]
) => {
  const showProductSpecial = shouldProductShowSpecial(product, userSegments);
  const {
    aggregate_rating,
    applicable_brand_special_ids,
    applicable_special_ids,
    available_weights,
    brand,
    brand_special_prices,
    brand_subtype,
    category,
    has_brand_discount,
    id,
    kind,
    name,
    percent_cbd,
    percent_thc,
    primaryPercentCBD,
    primaryPercentTHC,
    product_brand_id,
    product_id,
    review_count,
    root_subtype,
    special_id,
  } = product;

  let brandDiscountId: number | string | undefined;
  if (showProductSpecial) {
    brandDiscountId = applicable_brand_special_ids?.[0] ?? undefined;
    if (brand_special_prices) {
      const brandDiscountPriceWithId = Object.values(
        brand_special_prices
      ).filter((price) => !!price?.brand_special_id);
      brandDiscountId =
        brandDiscountPriceWithId.length > 0
          ? (brandDiscountPriceWithId[0]?.brand_special_id ?? brandDiscountId)
          : brandDiscountId;
    }
  }

  const specialIds = applicable_special_ids
    ? [...applicable_special_ids]
    : null;
  let hasSpecial = specialIds ? specialIds.length > 0 : undefined;
  hasSpecial = special_id ? !!special_id : hasSpecial;

  const hasMultipleWeights = available_weights
    ? available_weights.length > 1
    : undefined;

  return {
    brandDiscountId,
    brandId: product_brand_id,
    brandName: brand,
    hasBrandDiscount:
      (showProductSpecial && has_brand_discount) || !!brandDiscountId,
    hasMultipleWeights,
    hasSpecial,
    productId: id?.toString() || product_id?.toString() || null,
    productName: name,
    productBrand: brand,
    productKind: kind,
    productRootSubtype: root_subtype,
    productBrandSubtype: brand_subtype,
    productLineage: category,
    productPercentCBD: percent_cbd || primaryPercentCBD,
    productPercentTHC: percent_thc || primaryPercentTHC,
    productAggregateRating: aggregate_rating,
    productReviewCount: review_count,
    specialIds,
  };
};
